import moment from 'moment';
import { 
  ScheduleBlock as GrpcScheduleBlock,
  AlternateSchedule as GrpcAlternateSchedule,
  RegularScheduleResponse,
  MaterializedSchedule as GrpcMaterializedSchedule,
  ScheduleType as GrpcScheduleType
} from '../../grpc/site_pb';
import { iso8601Time } from '../../constants';
import { toLocalDateTime, toLocalDate } from '@optimusride/client-library/utils';


export enum ScheduleType {
  UNKNOWN_SCHEDULE,
  REGULAR_SCHEDULE,
  ALTERNATE_SCHEDULE,
  CANCELLATION,
  NO_SCHEDULE,
}

export interface AlternateSchedule {
  date: string;
  blocks: ScheduleBlock[];
}

export interface RegularSchedule {
  daysOfWweek: number[];
  blocks: ScheduleBlock[];
  startDate: Date;
}

export interface MaterializedSchedule {
  blocks: ScheduleBlock[];
  type: ScheduleType;
  date: string;
}

export interface ScheduleBlock {
  interval: string;
  seatCap: number;
  start: string;
  end: string;
  leadTime: string;
}

function toScheduleBlock(block: GrpcScheduleBlock): ScheduleBlock {
  return {
    interval: block.getInterval(),
    seatCap: block.getSeatCap(),
    start: block.getStart(),
    end: block.getEnd(),
    leadTime: block.getLeadTime()
  };
}

export function materializedScheduleBlockToScheduleBlock(block: ScheduleBlock, operatingAreaTimezone: string) {
  return {
    ...block,
    start: toLocalDate(block.start, operatingAreaTimezone).format(iso8601Time),
    end: toLocalDate(block.end, operatingAreaTimezone).format(iso8601Time)
  };
}

export function toGrpcScheduleBlock(block: ScheduleBlock) {
  const updatedBlock = new GrpcScheduleBlock();
  updatedBlock.setStart(block.start);
  updatedBlock.setEnd(block.end);
  updatedBlock.setInterval(block.interval);
  updatedBlock.setSeatCap(block.seatCap);
  updatedBlock.setLeadTime(block.leadTime);
  return updatedBlock;
}

export function toGrpcMaterializedScheduleBlock(block: ScheduleBlock, date: string, operatingAreaTimezone: string) {
  const scheduleBlockDateTime = (time: string) => toLocalDateTime(date, time, operatingAreaTimezone).format();
  const updatedBlock = new GrpcScheduleBlock();
  updatedBlock.setStart(scheduleBlockDateTime(block.start));
  updatedBlock.setEnd(scheduleBlockDateTime(block.end));
  updatedBlock.setInterval(block.interval);
  updatedBlock.setSeatCap(block.seatCap);
  updatedBlock.setLeadTime(block.leadTime);
  return updatedBlock;
}

export const defaultScheduleBlock = (): ScheduleBlock => ({
  start: "07:00",
  end: "10:00",
  interval: "PT30M",
  leadTime: "PT45M",
  seatCap: 1,
})

export const EmptyRegularSchedule: RegularSchedule = {
  daysOfWweek: [],
  blocks: [],
  startDate: new Date(),
}

export function toAlternateSchedule(schedule: GrpcAlternateSchedule): AlternateSchedule {
  return {
    date: schedule.getDate(),
    blocks: schedule.getScheduleBlocksList().map(toScheduleBlock)
  };
}

export function toRegularSchedule(schedule: RegularScheduleResponse | undefined): RegularSchedule {
  return schedule ? {
    daysOfWweek: schedule.getDaysOfWeekList(),
    blocks: schedule.getScheduleBlocksList().map(toScheduleBlock),
    startDate: moment(schedule.getStartDate()).toDate(),
  } : EmptyRegularSchedule;
}

export function toMaterializedSchedule(schedule: GrpcMaterializedSchedule): MaterializedSchedule {
  return {
    date: schedule.getDate(),
    blocks: schedule.getScheduleBlocksList().map(toScheduleBlock),
    type: toScheduleType(schedule.getScheduleType())
  };
}

function toScheduleType(type: GrpcScheduleType) {
  switch(type) {
    case GrpcScheduleType.NO_SCHEDULE:
      return ScheduleType.NO_SCHEDULE;
    case GrpcScheduleType.ALTERNATE_SCHEDULE:
      return ScheduleType.ALTERNATE_SCHEDULE;
    case GrpcScheduleType.REGULAR_SCHEDULE:
      return ScheduleType.REGULAR_SCHEDULE;
    case GrpcScheduleType.CANCELLATION:
      return ScheduleType.CANCELLATION;
    case GrpcScheduleType.UNKNOWN_SCHEDULE:
      return ScheduleType.UNKNOWN_SCHEDULE;
    default:
      throw new Error('Unknown Schedule Type enum value');
  }
}
